import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PexelsMediaItem, searchMedia, getInitialMedia } from '../../api/pexelsApi';

// Local debounce hook (private copy — TendV2ContentCard keeps its own for other uses)
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export interface PexelsMediaPickerProps {
  onSelectMedia: (media: PexelsMediaItem) => void;
  onLocalUpload: () => void;
  onClose: () => void;
  isUploading: boolean;
  aspectRatio?: string; // e.g., '1/1', '4/5', '9/16' - if not provided, use natural aspect ratio
  instanceId?: string; // Unique ID to prevent duplicate key warnings across multiple instances
  allowedType?: 'photos' | 'videos' | 'both'; // lock results to one media type (hides the toggle)
}

// Pexels Media Picker Panel component
export const PexelsMediaPicker = ({
  onSelectMedia,
  onLocalUpload,
  onClose,
  isUploading,
  aspectRatio,
  instanceId = 'default',
  allowedType = 'both',
}: PexelsMediaPickerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaType, setMediaType] = useState<'photos' | 'videos' | 'both'>(allowedType);
  const [results, setResults] = useState<PexelsMediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hoveredVideoId, setHoveredVideoId] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  const debouncedQuery = useDebounce(searchQuery, 400);

  // Escape closes the panel (companion to the X — mouse-leave no longer does)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset page when query or type changes
  useEffect(() => {
    setPage(1);
    setResults([]);
    setHasMore(true);
  }, [debouncedQuery, mediaType]);

  // Load media
  useEffect(() => {
    const loadMedia = async () => {
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      try {
        const response = debouncedQuery.trim()
          ? await searchMedia(debouncedQuery, mediaType, { page, perPage: 12 })
          : await getInitialMedia(mediaType, { page, perPage: 12 });

        if (page === 1) {
          setResults(response.media);
        } else {
          setResults(prev => [...prev, ...response.media]);
        }
        setHasMore(response.hasMore);
      } catch (error) {
        console.error('Error loading Pexels media:', error);
        if (page === 1) setResults([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    };
    loadMedia();
  }, [debouncedQuery, mediaType, page]);

  // Infinite scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;

    if (scrolledToBottom && hasMore && !isLoading && !isLoadingMore) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, isLoading, isLoadingMore]);

  const typeIcons = {
    photos: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    videos: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    both: (
      <div className="flex items-center gap-0.5">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
  };

  return (
    // Explicit close only (X / Escape) — the old onMouseLeave close made the
    // panel vanish on any pointer slip, and opening the OS file dialog
    // guarantees the pointer "leaves", tearing the panel down mid-upload.
    <div className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm rounded-lg flex flex-col overflow-hidden">
      {/* Header with search and controls */}
      <div className="flex items-center gap-2 p-3">
        {/* Search input with embedded toggle */}
        <div className="flex-1 min-w-0 relative flex items-center bg-black/50 rounded-full">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search media"
            className="flex-1 min-w-0 bg-transparent text-white placeholder-white/50 placeholder:italic px-4 py-2 text-sm outline-none border-none ring-0 focus:ring-0 focus:outline-none"
            style={{ boxShadow: 'none' }}
            autoFocus
          />

          {/* Media type toggle inside search bar (hidden when locked to one type) */}
          {allowedType === 'both' && (
          <div className="relative mr-1.5">
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 transition-colors"
            >
              {typeIcons[mediaType]}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showTypeDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-gray-900 rounded-lg shadow-lg border border-white/20 py-1 z-50 min-w-[110px]">
                {(['both', 'photos', 'videos'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setMediaType(type);
                      setShowTypeDropdown(false);
                    }}
                    className={`w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-white/10 ${
                      mediaType === type ? 'bg-white/20 text-white' : 'text-white/70'
                    }`}
                  >
                    {typeIcons[type]}
                    <span>{type === 'both' ? 'All' : type === 'photos' ? 'Images' : 'Videos'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Local upload button - circular dark with white icon */}
        <button
          onClick={onLocalUpload}
          disabled={isUploading}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-black/50 border border-white/20 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
          title="Upload from computer"
        >
          {isUploading ? (
            <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          )}
        </button>

        {/* Close button - circular dark with white icon */}
        <button
          onClick={onClose}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-black/50 border border-white/20 rounded-full hover:bg-white/10 transition-colors"
          title="Close"
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Media grid with masonry-like layout */}
      <div
        ref={gridRef}
        className="flex-1 overflow-auto p-3 pt-0"
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <svg className="w-8 h-8 animate-spin text-white/50" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : results.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-white/50 text-sm">
            No results found
          </div>
        ) : (
          <>
            {/* Use masonry (columns) for natural aspect ratio, grid for fixed */}
            <div className={aspectRatio ? 'grid grid-cols-3 gap-2' : 'columns-3 gap-2 space-y-2'}>
              {results.map((media) => {
                // Use platform's aspect ratio if provided, otherwise use natural aspect ratio
                const naturalRatio = media.width / media.height;
                const clampedNaturalRatio = Math.max(0.5, Math.min(2, naturalRatio));

                return (
                  <button
                    key={`${instanceId}-${media.type}-${media.id}`}
                    onClick={() => {
                      // Clear video hover state first to prevent ResizeObserver errors
                      setHoveredVideoId(null);
                      // Small delay to allow video element to unmount cleanly
                      setTimeout(() => onSelectMedia(media), 50);
                    }}
                    onMouseEnter={() => media.type === 'video' && setHoveredVideoId(media.id)}
                    onMouseLeave={() => setHoveredVideoId(null)}
                    className={`relative rounded-lg overflow-hidden group hover:ring-2 hover:ring-white transition-all ${!aspectRatio ? 'break-inside-avoid mb-2 block w-full' : ''}`}
                    style={{ aspectRatio: aspectRatio || clampedNaturalRatio }}
                  >
                    {/* Show video on hover for video items */}
                    {media.type === 'video' && hoveredVideoId === media.id ? (
                      <video
                        src={media.fullUrl}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img
                        src={media.thumbnailUrl}
                        alt={media.attribution}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Video indicator (only show when not playing) */}
                    {media.type === 'video' && hoveredVideoId !== media.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <svg className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {/* Hover overlay with photographer name */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-white text-xs truncate block">{media.photographer}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Load more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <svg className="w-6 h-6 animate-spin text-white/50" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PexelsMediaPicker;
